import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { projectsAPI } from "@/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  Calendar,
  User,
  Check,
  AlertCircle,
  Clock,
  Ban,
  Search,
  ChevronsUpDown,
  ArrowDownNarrowWide,
  ArrowDownUp,
  Image
} from "lucide-react";
import { MonoValue } from "@/components/redesign/MonoValue";
import { StatusBadge } from "@/components/redesign/StatusBadge";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Checkbox } from "@/components/ui/checkbox";
import { peopleAPI } from "@/api/people";
import { CalendarIcon } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import CheckStatusDialog from "@/components/CheckStatusDialog";
import { useAuth } from "@/contexts/AuthContext";
import { entriesAPI } from "@/api/entries";
import { useToast } from "@/hooks/use-toast";
import ImageViewDialog from "@/components/ImageViewDialog";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { getCurrencySymbol } from "@/utils/currencyUtils";
import { currencyAPI } from "@/api/currency";
import { formatInDeviceTimezone, adjustDateFromDB } from "@/utils/dateUtils";

interface CheckPaymentsTabProps {
  projectId: string;
  projectStatus: string;
}

const CheckPaymentsTab: React.FC<CheckPaymentsTabProps> = ({
  projectId,
  projectStatus,
}) => {
  const { t } = useTranslation();
  
  // Add debugging
  

  const [checks, setChecks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<any[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [showGivenChecks, setShowGivenChecks] = useState(true);
  const [showReceivedChecks, setShowReceivedChecks] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCheck, setSelectedCheck] = useState<any | null>(null);
  const [checkStatusDialogOpen, setCheckStatusDialogOpen] = useState(false);
  const [viewingImage, setViewingImage] = useState<{ url: string; title: string } | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [sortBy, setSortBy] = useState<"date" | "dueDate" | "amount">("dueDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const { preferences } = useUserPreferences();
  
  // Get the preferred currency and its symbol
  const preferredCurrency = preferences?.base_currency || "TRY";
  const currencySymbol = getCurrencySymbol(preferredCurrency);

  // Helper to standardize currency codes
  const standardizeCurrencyCode = (currency: string | null | undefined): string => {
    if (!currency) return "TRY";
    
    // Make sure currency codes are uppercase for the API
    return currency.toUpperCase();
  };

  // Helper to deeply inspect check objects for debugging
  const inspectCheckObject = (check: any, label: string) => {
    
  };

  // Load check payments and people
  useEffect(() => {
    
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        
        
        // Get all check payments for this project
        const checksData = await projectsAPI.getAllProjectCheckPayments(projectId);
        
        
        // If checks array is not valid, handle it gracefully
        if (!Array.isArray(checksData)) {
          
          setChecks([]);
        } else {
          // Deep inspect the first few check objects
          if (checksData.length > 0) {
            inspectCheckObject(checksData[0], "FIRST RAW CHECK");
            if (checksData.length > 1) {
              inspectCheckObject(checksData[1], "SECOND RAW CHECK");
            }
          }
          
          // If the preferred currency is not TRY, convert the check amounts
          if (preferredCurrency !== "TRY") {
            
            
            try {
              // Get all exchange rates
              const exchangeRates = await currencyAPI.getExchangeRates();
              
              
              const convertedChecks = await Promise.all(
                checksData.map(async (check) => {
                  if (!check || !check.id) {
                    
                    return null; // Skip invalid checks
                  }
                  
                  try {
                    
                    
                    // Deep inspect the check before conversion
                    inspectCheckObject(check, "BEFORE CONVERSION");
                    
                    const fromCurrency = standardizeCurrencyCode(check.currency);
                    const toCurrency = standardizeCurrencyCode(preferredCurrency);
                    
                    // If the currencies are the same, no need to convert
                    if (fromCurrency === toCurrency) {
                      
                      return check;
                    }
                    
                    // Make sure amount is a valid number
                    if (typeof check.amount !== 'number' || isNaN(check.amount)) {
                      
                      // Return check with 0 amount
                      return {
                        ...check,
                        amount: 0
                      };
                    }
                    
                    // Perform the conversion
                    try {
                      // Use the convertCurrency method
                      const { data: convertedAmount } = await currencyAPI.convertCurrency(
                        check.amount,
                        fromCurrency,
                        toCurrency
                      );
                      
                      
                      
                      const result = {
                        ...check,
                        // If conversion succeeded, use the converted amount
                        originalAmount: check.amount,
                        originalCurrency: fromCurrency,
                        amount: convertedAmount !== null ? convertedAmount : (typeof check.amount === 'number' ? check.amount : 0),
                        currency: toCurrency
                      };
                      
                      // Deep inspect the result after conversion
                      inspectCheckObject(result, "AFTER SUCCESSFUL CONVERSION");
                      
                      return result;
                    } catch (conversionError) {
                      
                      // Return original check on conversion error
                      return check;
                    }
                  } catch (checkProcessingError) {
                    
                    return check; // Return original check on error
                  }
                })
              );
              
              
              
              // Filter out null/undefined values
              const validChecks = convertedChecks.filter(Boolean);
              
              // Deep inspect the first converted check
              if (validChecks.length > 0) {
                inspectCheckObject(validChecks[0], "FIRST CONVERTED CHECK BEFORE SETTING STATE");
              }
              
              setChecks(validChecks);
            } catch (conversionSetupError) {
              
              // Fall back to original checks
              setChecks(checksData.filter(check => check && check.id));
            }
          } else {
            
            // Filter out null/undefined values
            setChecks(checksData.filter(check => check && check.id));
          }
        }

        // Get all people associated with this project
        
        try {
          const peopleData = await peopleAPI.getPeopleByProject(projectId);
          
          setPeople(Array.isArray(peopleData) ? peopleData : []);
        } catch (peopleError) {
          
          setPeople([]);
        }
      } catch (error) {
        
        setError(t('check_payments_tab.load_error'));
        setChecks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, preferredCurrency, t]);

  // Filter and sort checks
  const filteredChecks = checks.filter(check => {
    if (!check) {
      
      return false; // Skip null/undefined check objects
    }
  
    // Filter by check type
    if (!showGivenChecks && check.isGivenCheck) return false;
    if (!showReceivedChecks && !check.isGivenCheck) return false;

    // Filter by person
    if (selectedPerson !== "all" && check.personId !== selectedPerson) return false;

    // Filter by date range
    if (startDate && check.date && new Date(check.date) < startDate) return false;
    if (endDate && check.date) {
      const endDateWithTime = new Date(endDate);
      endDateWithTime.setHours(23, 59, 59, 999);
      if (new Date(check.date) > endDateWithTime) return false;
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const description = check.description ? check.description.toLowerCase() : "";
      const personName = check.personName ? check.personName.toLowerCase() : "";
      const checkNumber = check.checkNumber ? check.checkNumber.toLowerCase() : "";
      
      if (!description.includes(query) && 
          !personName.includes(query) && 
          !checkNumber.includes(query)) {
        return false;
      }
    }

    return true;
  }).sort((a, b) => {
    let aValue, bValue;
    
    if (sortBy === "date") {
      aValue = a.date ? new Date(a.date).getTime() : 0;
      bValue = b.date ? new Date(b.date).getTime() : 0;
    } else if (sortBy === "dueDate") {
      // Handle null due dates by putting them at the end
      if (!a.dueDate) return sortOrder === "asc" ? 1 : -1;
      if (!b.dueDate) return sortOrder === "asc" ? -1 : 1;
      
      aValue = new Date(a.dueDate).getTime();
      bValue = new Date(b.dueDate).getTime();
    } else {
      // Sort by amount
      aValue = a.amount || 0;
      bValue = b.amount || 0;
    }
    
    if (sortOrder === "asc") {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });
  
  
  
  // Debug check filters and data
  useEffect(() => {
    // Log only if there seems to be an issue with data display
    if (checks.length > 0 && filteredChecks.length === 0) {
      console.warn('CheckPaymentsTab: Data exists but filtered out. Check filters:', {
        totalChecks: checks.length,
        showGivenChecks,
        showReceivedChecks,
        selectedPerson,
        startDate,
        endDate,
        searchQuery
      });
    }
  }, [checks, filteredChecks, showGivenChecks, showReceivedChecks, selectedPerson, startDate, endDate, searchQuery]);

  const handleCheckStatusClick = (check: any) => {
    setSelectedCheck(check);
    setCheckStatusDialogOpen(true);
  };

  const handleCheckStatusUpdate = async (status: 'alindi' | 'ertelendi' | 'alinamadi', newDueDate?: Date) => {
    if (!selectedCheck || !user?.id) return;

    try {
      // Update check status
      await entriesAPI.updateCheckStatus(
        selectedCheck.id,
        status,
        newDueDate,
        user.id
      );
      
      // Show success toast based on status
      let message = "";
      if (status === 'alindi') {
        message = selectedCheck.isGivenCheck 
          ? t('check_payments_tab.check_paid')
          : t('check_payments_tab.check_collected');
      } else if (status === 'ertelendi') {
        message = t('check_payments_tab.check_postponed');
      } else {
        message = selectedCheck.isGivenCheck
          ? t('check_payments_tab.check_not_paid')
          : t('check_payments_tab.check_not_collected');
      }
      
      toast({
        title: t('check_payments_tab.check_status_updated'),
        description: message,
      });
      
      // We're refreshing the data, so let's set loading to true
      setLoading(true);
      
      // Immediately update the current check in the local state to show as processed
      if (status === 'alindi' || status === 'alinamadi') {
        // Find the check in the current state and mark it as processed
        setChecks(prevChecks => 
          prevChecks.map(check => 
            check.id === selectedCheck.id 
              ? {
                  ...check, 
                  isProcessed: true,
                  status: status === 'alindi' ? 'completed' : 'failed',
                  processed_at: new Date().toISOString(),
                  processed_by: user.id
                } 
              : check
          )
        );
      }
      
      // Refresh checks - simply re-fetch the data from scratch to ensure consistency
      try {
        const checksData = await projectsAPI.getAllProjectCheckPayments(projectId);
        
        // If the preferred currency is not TRY, convert the check amounts
        if (preferredCurrency !== "TRY") {
          const convertedChecks = await Promise.all(
            checksData.map(async (check) => {
              if (!check.amount && check.amount !== 0) {
                
                return {
                  ...check,
                  amount: 0 // Set a fallback amount of 0
                };
              }
              
              try {
                // Standardize currency codes
                const fromCurrency = standardizeCurrencyCode(check.currency);
                const toCurrency = standardizeCurrencyCode(preferredCurrency);
                
                // If the currencies are the same, no need to convert
                if (fromCurrency === toCurrency) {
                  return {
                    ...check,
                    // Store original values for reference
                    originalAmount: check.amount,
                    originalCurrency: fromCurrency,
                    currency: toCurrency
                  };
                }
                
                // Perform the conversion
                const { data: convertedAmount, error } = await currencyAPI.convertCurrency(
                  check.amount,
                  fromCurrency,
                  toCurrency
                );
                
                if (error) {
                  
                  // Return original check but with fallback amount of 0 if amount is invalid
                  return {
                    ...check,
                    amount: typeof check.amount === 'number' ? check.amount : 0
                  };
                }
                
                return {
                  ...check,
                  // If conversion succeeded, use the converted amount
                  originalAmount: check.amount,
                  originalCurrency: fromCurrency,
                  amount: convertedAmount !== null ? convertedAmount : (typeof check.amount === 'number' ? check.amount : 0),
                  currency: toCurrency
                };
              } catch (error) {
                
                // Return original check but with fallback amount of 0 if amount is invalid
                return {
                  ...check,
                  amount: typeof check.amount === 'number' ? check.amount : 0
                };
              }
            })
          );
          
          setChecks(convertedChecks);
        } else {
          setChecks(checksData);
        }
      } catch (error) {
        
        toast({
          title: t('common.error'),
          description: t('check_payments_tab.check_update_error'),
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('check_payments_tab.check_update_error'),
        variant: "destructive"
      });
      setLoading(false);
    } finally {
      setCheckStatusDialogOpen(false);
      setSelectedCheck(null);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleSortChange = (newSortBy: "date" | "dueDate" | "amount") => {
    if (sortBy === newSortBy) {
      toggleSortOrder();
    } else {
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
  };

  const getStatusBadge = (check: any) => {
    if (check.isProcessed) {
      if (check.status === "completed") {
        return (
          <StatusBadge tone="success">
            {check.isGivenCheck ? t('check_payments_tab.status.paid') : t('check_payments_tab.status.collected')}
          </StatusBadge>
        );
      } else if (check.status === "failed") {
        return (
          <StatusBadge tone="error">
            {check.isGivenCheck ? t('check_payments_tab.status.could_not_pay') : t('check_payments_tab.status.could_not_collect')}
          </StatusBadge>
        );
      }
    }

    if (check.isPostponed) {
      return <StatusBadge tone="warning">{t('check_payments_tab.status.postponed')}</StatusBadge>;
    }

    if (check.isOverdue) {
      return <StatusBadge tone="error">{t('check_payments_tab.status.overdue')}</StatusBadge>;
    }

    return (
      <StatusBadge tone="brand">
        {check.dueInDays === 0 ? t('check_payments_tab.status.today') : t('check_payments_tab.status.days_left', { days: check.dueInDays })}
      </StatusBadge>
    );
  };

  const resetFilters = () => {
    setSelectedPerson("all");
    setStartDate(undefined);
    setEndDate(undefined);
    setShowGivenChecks(true);
    setShowReceivedChecks(true);
    setSearchQuery("");
  };

  // Function to handle viewing an image
  const handleViewImage = (url: string, title: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!url) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('check_payments_tab.image_not_found')
      });
      return;
    }
    setViewingImage({ 
      url, 
      title: title || t('check_payments_tab.view_image')
    });
  };

  const formatCurrencyValue = (value: number): string => {
    if (!value) return "0";
    
    // Set decimal places based on currency
    let decimalPlaces = 2; // Default for most currencies
    if (preferredCurrency === 'XAU') decimalPlaces = 4;
    if (preferredCurrency === 'BTC') decimalPlaces = 6;
    
    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    });
  };

  // Display the correct currency format based on the preferred currency
  const displayCurrencyAmount = (value: number) => {
    const formattedValue = formatCurrencyValue(value);
    
    // For special currencies, add the appropriate suffix
    if (preferredCurrency === 'XAU') {
      return `${formattedValue} Ons`;
    }
    
    // For other currencies, just display the value
    return formattedValue;
  };

  return (
    <div className="pt-2">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm mb-4 overflow-hidden">
        <div className="px-4 pt-4 pb-2 border-b border-gray-100">
          <h3 className="text-[15px] font-bold text-gray-900">{t('check_payments_tab.title')}</h3>
          <p className="text-[13px] text-gray-500 mt-0.5">{t('check_payments_tab.description')}</p>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {/* Search and filters */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('check_payments_tab.search_placeholder')}
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="givenChecks" 
                    checked={showGivenChecks}
                    onCheckedChange={() => setShowGivenChecks(!showGivenChecks)}
                  />
                  <Label htmlFor="givenChecks">{t('check_payments_tab.given_checks')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="receivedChecks" 
                    checked={showReceivedChecks}
                    onCheckedChange={() => setShowReceivedChecks(!showReceivedChecks)}
                  />
                  <Label htmlFor="receivedChecks">{t('check_payments_tab.received_checks')}</Label>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="person-filter">{t('check_payments_tab.person_filter')}</Label>
                <Select
                  value={selectedPerson}
                  onValueChange={setSelectedPerson}
                >
                  <SelectTrigger id="person-filter" className="w-full">
                    <SelectValue placeholder={t('check_payments_tab.person_filter_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('check_payments_tab.all_people')}</SelectItem>
                    {people.map(person => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1">
                <Label>{t('check_payments_tab.start_date')}</Label>
                <DatePicker
                  date={startDate}
                  onSelect={setStartDate}
                  placeholder={t('check_payments_tab.start_date_placeholder')}
                />
              </div>

              <div className="flex-1">
                <Label>{t('check_payments_tab.end_date')}</Label>
                <DatePicker
                  date={endDate}
                  onSelect={setEndDate}
                  placeholder={t('check_payments_tab.end_date_placeholder')}
                />
              </div>

              <div className="flex items-end">
                <Button variant="outline" onClick={resetFilters}>
                  {t('check_payments_tab.clear_filters')}
                </Button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {t('check_payments_tab.checks_found', { count: filteredChecks.length })}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleSortChange("date")}
                  className={sortBy === "date" ? "border-primary" : ""}
                >
                  {t('check_payments_tab.sort_by_date')}
                  {sortBy === "date" && (
                    sortOrder === "asc" ? 
                    <ArrowDownNarrowWide className="ml-1 h-4 w-4" /> : 
                    <ChevronsUpDown className="ml-1 h-4 w-4" />
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleSortChange("dueDate")}
                  className={sortBy === "dueDate" ? "border-primary" : ""}
                >
                  {t('check_payments_tab.sort_by_due_date')}
                  {sortBy === "dueDate" && (
                    sortOrder === "asc" ? 
                    <ArrowDownNarrowWide className="ml-1 h-4 w-4" /> : 
                    <ChevronsUpDown className="ml-1 h-4 w-4" />
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleSortChange("amount")}
                  className={sortBy === "amount" ? "border-primary" : ""}
                >
                  {t('check_payments_tab.sort_by_amount')}
                  {sortBy === "amount" && (
                    sortOrder === "asc" ? 
                    <ArrowDownNarrowWide className="ml-1 h-4 w-4" /> : 
                    <ChevronsUpDown className="ml-1 h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-50 border border-error-100 text-error-600 p-4 rounded-xl mb-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="flex flex-col items-center">
            <Clock className="h-8 w-8 animate-spin text-brand-600 mb-2" />
            <p className="text-gray-500">{t('check_payments_tab.loading')}</p>
          </div>
        </div>
      ) : filteredChecks.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-2xl">
          <CreditCard className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h3 className="text-[15px] font-semibold text-gray-900 mb-1">{t('check_payments_tab.no_checks')}</h3>
          <p className="text-[13px] text-gray-500">{t('check_payments_tab.no_checks_description')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredChecks.map(check => {
            return (
              <div
                key={check.id}
                className={cn(
                  "bg-white border rounded-2xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow",
                  check.isProcessed
                    ? check.status === "completed" ? "border-success-200" : "border-error-100"
                    : check.isOverdue ? "border-error-100" : "border-gray-200"
                )}
                onClick={() => navigate(`/entry/${check.id}`)}
              >
                <div className="flex flex-col md:flex-row justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <CreditCard className={cn("h-4 w-4 shrink-0", check.isGivenCheck ? "text-warning-600" : "text-success-600")} />
                      <span className="text-[13px] font-medium text-gray-700">
                        {check.isGivenCheck ? t('check_payments_tab.given_check') : t('check_payments_tab.received_check')}
                      </span>
                      {getStatusBadge(check)}
                    </div>

                    <h3 className="text-[15px] font-semibold text-gray-900 mb-1">{check.description}</h3>

                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <MonoValue
                          value={check.date ? formatInDeviceTimezone(adjustDateFromDB(check.date), "dd.MM.yyyy", { locale: tr }) : t('check_payments_tab.date_not_specified')}
                          tone="muted"
                          size="meta"
                        />
                      </div>

                      {check.dueDate && (
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          <span>{t('check_payments_tab.due_date')}</span>
                          <MonoValue
                            value={formatInDeviceTimezone(adjustDateFromDB(check.dueDate), "dd.MM.yyyy", { locale: tr })}
                            tone="muted"
                            size="meta"
                          />
                        </div>
                      )}

                      {check.personName && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{check.personName}</span>
                        </div>
                      )}

                      {check.checkNumber && (
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-3 w-3" />
                          <span>{t('check_payments_tab.check_number')}</span>
                          <MonoValue value={check.checkNumber} tone="muted" size="meta" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-2">
                    <MonoValue
                      value={check.amount || 0}
                      prefix={check.currency === 'USD' ? '$' : check.currency === 'EUR' ? '€' : check.currency === 'BTC' ? '₿' : '₺'}
                      suffix={check.currency === 'XAU' ? ' XAU' : check.currency && !['USD', 'EUR', 'BTC', 'TRY'].includes(check.currency) ? ` ${check.currency}` : undefined}
                      format
                      fractionDigits={check.currency === 'XAU' ? 4 : check.currency === 'BTC' ? 6 : 2}
                      tone={check.isGivenCheck ? "error" : "success"}
                      size="md"
                    />
                    <div className="flex gap-1.5">
                      {(check.imageUrl || check.image_url) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] border-gray-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            const imageUrl = check.imageUrl || check.image_url;
                            handleViewImage(imageUrl, check.description || t('check_payments_tab.view_image'), e);
                          }}
                        >
                          <Image className="h-3 w-3 mr-1" /> {t('check_payments_tab.view_image')}
                        </Button>
                      )}

                      {!check.isProcessed && projectStatus !== 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] border-brand-200 text-brand-600 hover:bg-brand-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCheckStatusClick(check);
                          }}
                        >
                          {t('check_payments_tab.update_status')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Check Status Dialog */}
      {selectedCheck && (
        <CheckStatusDialog
          open={checkStatusDialogOpen}
          onOpenChange={setCheckStatusDialogOpen}
          isGivenCheck={selectedCheck.isGivenCheck}
          checkId={selectedCheck.id}
          checkAmount={selectedCheck.amount}
          checkDescription={selectedCheck.description}
          onStatusUpdate={handleCheckStatusUpdate}
        />
      )}

      {/* Image Viewing Dialog */}
      {viewingImage && (
        <ImageViewDialog
          imageUrl={viewingImage.url}
          title={viewingImage.title}
          open={!!viewingImage}
          onOpenChange={(open) => {
            if (!open) setViewingImage(null);
          }}
        />
      )}
    </div>
  );
};

export default CheckPaymentsTab; 